// Automation template types for partner-dashboard
// These match the structure from internal-dashboard

import {
  type AutomationTemplateContents,
  type AutomationTemplateListItemResponse,
  type AutomationTemplateResponse,
  type AutomationTemplateTenants,
  AutomationTemplateTenantsModeEnum,
} from '@reach/shared-types/automation-template';

/**
 * UI-level tenant selection mode:
 * - 'all': sync to all current and future tenants (maps to targetedTenantsFilter: null)
 * - 'selected': sync only to selected tenants (maps to targetedTenantsFilter: string[])
 */
export type TenantSelectionType = 'all' | 'selected';

/**
 * Tenant selection shared by the automation template and segment editors. `excludedTenantIds`
 * only applies when `selectionType` is 'all'; 'selected' with no ids is the draft state.
 */
export interface TenantSelection {
  selectionType: TenantSelectionType;
  selectedTenantIds: string[];
  excludedTenantIds: string[];
}

/**
 * Builds the UI tenant selection from the backend targeting (`targetedTenantsFilter`: null=all,
 * []=none, string[]=filtered; `excludedTenantIds` for all). Used when loading a template or segment.
 */
export function toTenantSelection(
  targetedTenantsFilter: string[] | null | undefined,
  excludedTenantIds: string[] | null | undefined,
): TenantSelection {
  const selectionType: TenantSelectionType = targetedTenantsFilter == null ? 'all' : 'selected';
  return {
    selectionType,
    selectedTenantIds: targetedTenantsFilter ?? [],
    excludedTenantIds: selectionType === 'all' ? (excludedTenantIds ?? []) : [],
  };
}

/**
 * Builds the UI tenant selection from a template's `tenants`. Used when loading a template into the
 * editor.
 */
export function toTemplateTenantSelection(tenants: AutomationTemplateTenants): TenantSelection {
  if (tenants.mode === AutomationTemplateTenantsModeEnum.ALL) {
    return { selectionType: 'all', selectedTenantIds: [], excludedTenantIds: tenants.excludedTenantIds };
  }
  return { selectionType: 'selected', selectedTenantIds: tenants.tenantIds, excludedTenantIds: [] };
}

/**
 * Converts a UI tenant selection to the API's targeting fields. Used by the template and segment save paths.
 */
export function toTenantTargetingPayload(selection: TenantSelection): {
  targetedTenantsFilter: string[] | null;
  excludedTenantIds: string[] | null;
} {
  if (selection.selectionType === 'all') {
    return { targetedTenantsFilter: null, excludedTenantIds: selection.excludedTenantIds };
  }
  return { targetedTenantsFilter: selection.selectedTenantIds, excludedTenantIds: null };
}

function haveSameIds(first: string[], second: string[]): boolean {
  const secondIds = new Set(second);
  return first.length === secondIds.size && first.every(id => secondIds.has(id));
}

/**
 * Whether two selections target different tenants. Used for the editors' unsaved-changes checks.
 */
export function tenantSelectionsDiffer(first: TenantSelection, second: TenantSelection): boolean {
  if (first.selectionType !== second.selectionType) {
    return true;
  }
  return first.selectionType === 'all'
    ? !haveSameIds(first.excludedTenantIds, second.excludedTenantIds)
    : !haveSameIds(first.selectedTenantIds, second.selectedTenantIds);
}

/** `activeBusinessIds` and `categories` are present only on list responses. */
export type AutomationTemplate = AutomationTemplateResponse &
  Partial<Pick<AutomationTemplateListItemResponse, 'activeBusinessIds' | 'categories'>>;

export interface CreateAutomationTemplateParams {
  name: string;
  version: string;
  contents: AutomationTemplateContents;
}

export interface UpdateAutomationTemplateParams {
  name?: string;
  version?: string;
  contents?: Partial<AutomationTemplateContents>;
  targetedTenantsFilter?: string[] | null;
  excludedTenantIds?: string[] | null;
  /** The tenant targeting this editor loaded, so the save keeps tenants changed since then. */
  previousTargetedTenantsFilter?: string[] | null;
  previousExcludedTenantIds?: string[] | null;
}

export interface TemplatesResponse {
  templates: AutomationTemplateListItemResponse[];
}

export interface TemplateResponse {
  template: AutomationTemplate;
}

export interface DuplicateDefaultsResponse {
  success: boolean;
  createdCount: number;
  templateIds?: string[];
}

export interface TenantSegmentConflict {
  tenantId: string;
  tenantName: string;
  missingSegments: Array<{ canonicalVersionId: string; name: string }>;
}

export interface ValidateTenantSegmentsResponse {
  conflicts: TenantSegmentConflict[];
}

export interface TenantSelectionConflict {
  tenantId: string;
  tenantName: string;
  description: string;
}

/** GET /sync/status/:templateId — returns status and subJobs when a matching active job exists. */
export interface CheckActiveSyncJobsResponse {
  hasActiveJobs: boolean;
  status?: string;
  subJobs?: Array<{ jobId: string; businessId: string | null; status: string }>;
}

export interface TenantAutomationStatus {
  id: string;
  status: string;
  dryRun: boolean;
}

export interface BusinessAutomationStatusRow {
  businessId: string;
  businessName: string;
  automations: TenantAutomationStatus[];
  statusCounts: Record<string, number>;
}

export interface AutomationStatusesResponse {
  businessStatuses: BusinessAutomationStatusRow[];
  totalAutomations: number;
}
